<template>
    <article class="staff-card" :class="{ 'is-deleted': item.isDeleted }">
        <header class="staff-card__head">
            <img
                class="staff-card__avatar"
                :src="item.passport || '/img/brand/loguser.png'"
                alt=""
            />

            <div class="staff-card__identity">
                <span class="staff-card__name">{{ item.staffName || "Unnamed staff" }}</span>
                <span class="staff-card__line">
                    <i class="fa fa-id-card-o" aria-hidden="true"></i>
                    {{ item.seviceNo || "No service number" }}
                </span>
                <span class="staff-card__line">
                    <i class="fa fa-envelope-o" aria-hidden="true"></i>
                    {{ item.emailAddress || "No email address" }}
                </span>
                <span class="staff-card__line">
                    <i class="fa fa-phone" aria-hidden="true"></i>
                    {{ item.phoneNumber || "No phone number" }}
                </span>

                <span class="staff-card__badges">
                    <span class="staff-status" :class="item.isDeleted ? 'is-deleted' : 'is-active'">
                        {{ item.isDeleted ? "Deleted" : "Active" }}
                    </span>
                    <span
                        v-if="item.syncStatus && item.syncStatus !== 'synced'"
                        class="staff-status is-sync"
                    >
                        <i class="fa" :class="syncIcon" aria-hidden="true"></i>
                        {{ syncLabel }}
                    </span>
                </span>

                <span
                    v-if="item.syncStatus === 'failed' && item.syncError"
                    class="staff-card__sync-error"
                >
                    {{ item.syncError }}
                </span>
            </div>

            <div class="staff-card__actions">
                <button
                    type="button"
                    class="ws-edit-button"
                    :title="isPendingSync ? 'Edit this pending change' : 'Edit staff record'"
                    :aria-label="`Edit ${item.staffName || 'staff member'}`"
                    @click="$emit('edit', item)"
                >
                    <i class="fa fa-pencil" aria-hidden="true"></i>
                    <span>Edit</span>
                </button>

                <template v-if="isPendingSync">
                    <button
                        type="button"
                        class="ws-danger-button"
                        title="Remove this pending change from this device"
                        :aria-label="`Discard pending change for ${item.staffName || 'staff member'}`"
                        @click="$emit('discard-pending', item)"
                    >
                        <i class="fa fa-ban" aria-hidden="true"></i>
                        <span>Discard pending</span>
                    </button>
                </template>

                <template v-else>
                    <button
                        v-if="item.isDeleted"
                        type="button"
                        class="ws-ghost-button"
                        title="Restore staff member"
                        :aria-label="`Restore ${item.staffName || 'staff member'}`"
                        @click="$emit('toggle-delete', item.id)"
                    >
                        <i class="fa fa-history" aria-hidden="true"></i>
                        <span>Restore</span>
                    </button>
                    <button
                        v-else
                        type="button"
                        class="ws-danger-button"
                        title="Delete staff member"
                        :aria-label="`Delete ${item.staffName || 'staff member'}`"
                        @click="$emit('toggle-delete', item.id)"
                    >
                        <i class="fa fa-trash" aria-hidden="true"></i>
                        <span>Delete</span>
                    </button>
                </template>
            </div>
        </header>

        <div class="staff-card__panels">
            <section class="staff-panel">
                <button
                    type="button"
                    class="staff-panel__toggle"
                    :aria-expanded="isClassesOpen ? 'true' : 'false'"
                    @click="toggleClasses"
                >
                    <span class="staff-panel__icon" aria-hidden="true">
                        <i class="fa fa-chalkboard-teacher"></i>
                    </span>
                    <span class="staff-panel__title">Classes</span>
                    <span class="staff-panel__count">{{ classAssigned.length }}</span>
                    <i
                        class="fa fa-chevron-down staff-panel__chevron"
                        :class="{ 'is-open': isClassesOpen }"
                        aria-hidden="true"
                    ></i>
                </button>

                <div v-show="isClassesOpen" class="staff-panel__content">
                    <div class="staff-panel__toolbar">
                        <button
                            type="button"
                            class="ws-ghost-button"
                            :disabled="isPendingSync"
                            :title="isPendingSync ? 'Push the pending sync first' : 'Assign a class'"
                            @click.stop="handleAddClass"
                        >
                            <i class="fa fa-plus" aria-hidden="true"></i>
                            <span>Add class</span>
                        </button>
                    </div>

                    <ul v-if="classAssigned.length" class="staff-panel__list">
                        <li
                            v-for="assignment in classAssigned"
                            :key="assignment.classAssignedId || assignment.className"
                        >
                            <div class="staff-panel__item">
                                <span class="staff-panel__item-title">
                                    {{ assignment.className || "Class" }}
                                </span>
                                <span class="staff-panel__item-meta">
                                    {{ assignment.sectionName || "No arm" }}
                                </span>
                            </div>
                            <button
                                type="button"
                                class="staff-panel__remove"
                                :aria-label="`Remove ${assignment.className || 'class'}`"
                                @click.stop="$emit('delete-class', assignment.classAssignedId)"
                            >
                                <i class="fa fa-times" aria-hidden="true"></i>
                            </button>
                        </li>
                    </ul>
                    <p v-else class="staff-panel__empty">No classes assigned yet.</p>
                </div>
            </section>

            <section class="staff-panel">
                <button
                    type="button"
                    class="staff-panel__toggle"
                    :aria-expanded="isSubjectsOpen ? 'true' : 'false'"
                    @click="toggleSubjects"
                >
                    <span class="staff-panel__icon" aria-hidden="true">
                        <i class="fa fa-book"></i>
                    </span>
                    <span class="staff-panel__title">Subjects</span>
                    <span class="staff-panel__count">{{ subjectAssigned.length }}</span>
                    <i
                        class="fa fa-chevron-down staff-panel__chevron"
                        :class="{ 'is-open': isSubjectsOpen }"
                        aria-hidden="true"
                    ></i>
                </button>

                <div v-show="isSubjectsOpen" class="staff-panel__content">
                    <div class="staff-panel__toolbar">
                        <button
                            type="button"
                            class="ws-ghost-button"
                            :disabled="isPendingSync"
                            :title="isPendingSync ? 'Push the pending sync first' : 'Assign a subject'"
                            @click.stop="handleAddSubject"
                        >
                            <i class="fa fa-plus" aria-hidden="true"></i>
                            <span>Add subject</span>
                        </button>
                    </div>

                    <ul v-if="subjectAssigned.length" class="staff-panel__list">
                        <li
                            v-for="assignment in subjectAssigned"
                            :key="assignment.subjectAssignedId || assignment.subjectName"
                        >
                            <div class="staff-panel__item">
                                <span class="staff-panel__item-title">
                                    {{ assignment.subjectName || "Subject" }}
                                </span>
                                <span class="staff-panel__item-meta">
                                    {{ assignment.className || "—" }}
                                    <span v-if="assignment.optionName"> · {{ assignment.optionName }}</span>
                                </span>
                            </div>
                            <button
                                type="button"
                                class="staff-panel__remove"
                                :aria-label="`Remove ${assignment.subjectName || 'subject'}`"
                                @click.stop="$emit('delete-subject', assignment.subjectAssignedId)"
                            >
                                <i class="fa fa-times" aria-hidden="true"></i>
                            </button>
                        </li>
                    </ul>
                    <p v-else class="staff-panel__empty">No subjects assigned yet.</p>
                </div>
            </section>
        </div>
    </article>
</template>

<script>
export default {
    name: "staff-card",
    props: {
        item: {
            type: Object,
            required: true
        }
    },
    data() {
        return {
            isClassesOpen: false,
            isSubjectsOpen: false
        };
    },
    computed: {
        classAssigned() {
            return Array.isArray(this.item.classAssigned) ? this.item.classAssigned : [];
        },
        subjectAssigned() {
            return Array.isArray(this.item.subjectAssigned) ? this.item.subjectAssigned : [];
        },
        isPendingSync() {
            return Boolean(this.item.syncStatus && this.item.syncStatus !== "synced");
        },
        syncIcon() {
            if (this.item.syncStatus === "syncing") return "fa-spinner fa-spin";
            if (this.item.syncStatus === "failed") return "fa-exclamation-triangle";
            return "fa-clock-o";
        },
        syncLabel() {
            if (this.item.syncStatus === "syncing") return "Syncing";
            if (this.item.syncStatus === "failed") return "Sync failed";
            return "Pending sync";
        }
    },
    methods: {
        toggleClasses() {
            this.isClassesOpen = !this.isClassesOpen;
        },
        toggleSubjects() {
            this.isSubjectsOpen = !this.isSubjectsOpen;
        },
        handleAddClass() {
            this.isClassesOpen = true;
            this.$emit("add-class", this.item);
        },
        handleAddSubject() {
            this.isSubjectsOpen = true;
            this.$emit("add-subject", this.item);
        }
    }
};
</script>

<style scoped>
.staff-card {
    display: flex;
    height: 100%;
    min-width: 0;
    flex-direction: column;
    overflow: hidden;
    border: 1px solid #e5eaf0;
    border-radius: 0.9rem;
    background: #fff;
    box-shadow: 0 0.4rem 1.15rem rgba(50, 70, 90, 0.07);
}

.staff-card.is-deleted {
    border-color: #efd5d8;
    background: #fffbfb;
}

.staff-card__head {
    display: flex;
    align-items: flex-start;
    gap: 0.85rem;
    padding: 1rem;
    border-bottom: 1px solid #edf0f3;
}

.staff-card__avatar {
    flex: 0 0 3rem;
    width: 3rem;
    height: 3rem;
    border-radius: 50%;
    background: #eef4ff;
    object-fit: cover;
}

.staff-card__identity {
    display: grid;
    min-width: 0;
    flex: 1 1 auto;
    gap: 0.18rem;
}

.staff-card__name {
    overflow-wrap: anywhere;
    color: #293b52;
    font-size: 1rem;
    font-weight: 700;
    line-height: 1.3;
}

.staff-card__line {
    display: flex;
    align-items: center;
    gap: 0.4rem;
    overflow-wrap: anywhere;
    color: #7b8998;
    font-size: 0.75rem;
    line-height: 1.4;
}

.staff-card__line i {
    width: 0.9rem;
    flex: 0 0 0.9rem;
    color: #9db6d4;
    text-align: center;
}

.staff-card__badges {
    display: flex;
    flex-wrap: wrap;
    gap: 0.35rem;
    margin-top: 0.2rem;
}

.staff-status {
    display: inline-flex;
    align-items: center;
    gap: 0.3rem;
    padding: 0.14rem 0.45rem;
    border-radius: 999px;
    font-size: 0.62rem;
    font-weight: 700;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    white-space: nowrap;
}

.staff-status.is-active {
    background: #eef4ff;
    color: #1850b8;
}

.staff-status.is-deleted {
    background: #fdf1f2;
    color: #b03a48;
}

.staff-status.is-sync {
    background: #fff8e9;
    color: #a36a16;
}

.staff-card__sync-error {
    display: -webkit-box;
    overflow: hidden;
    margin-top: 0.15rem;
    color: #b03a48;
    font-size: 0.68rem;
    line-height: 1.4;
    overflow-wrap: anywhere;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 2;
}

.staff-card__actions {
    display: flex;
    flex: 0 0 auto;
    flex-wrap: wrap;
    gap: 0.35rem;
}

.staff-card__actions .ws-edit-button,
.staff-card__actions .ws-danger-button,
.staff-card__actions .ws-ghost-button {
    margin: 0;
}

.staff-card__actions button:disabled {
    opacity: 0.5;
    cursor: not-allowed;
}

.staff-card__panels {
    display: grid;
    gap: 0.75rem;
    padding: 1rem;
}

.staff-panel {
    min-width: 0;
    overflow: hidden;
    border: 1px solid #e8edf3;
    border-radius: 0.7rem;
    background: #fff;
}

.staff-panel__toggle {
    display: flex;
    width: 100%;
    align-items: center;
    gap: 0.55rem;
    padding: 0.7rem 0.75rem;
    border: 0;
    background: #f8fafc;
    color: #344767;
    cursor: pointer;
    text-align: left;
}

.staff-panel__toggle:hover,
.staff-panel__toggle:focus {
    background: #f2f7fd;
    outline: none;
}

.staff-panel__icon {
    display: grid;
    flex: 0 0 1.75rem;
    width: 1.75rem;
    height: 1.75rem;
    place-items: center;
    border-radius: 0.45rem;
    background: #eef4ff;
    color: #1850b8;
    font-size: 0.76rem;
}

.staff-panel__title {
    flex: 1 1 auto;
    font-size: 0.78rem;
    font-weight: 700;
    letter-spacing: 0.04em;
    text-transform: uppercase;
}

.staff-panel__count {
    display: inline-flex;
    min-width: 1.4rem;
    justify-content: center;
    padding: 0.1rem 0.4rem;
    border-radius: 999px;
    background: #eef4ff;
    color: #2d6fae;
    font-size: 0.65rem;
    font-weight: 700;
}

.staff-panel__chevron {
    color: #9aa6b3;
    font-size: 0.7rem;
    transition: transform 0.2s ease;
}

.staff-panel__chevron.is-open {
    transform: rotate(180deg);
}

.staff-panel__content {
    padding: 0.7rem 0.75rem 0.8rem;
    border-top: 1px solid #edf0f3;
}

.staff-panel__toolbar {
    display: flex;
    justify-content: flex-end;
    margin-bottom: 0.55rem;
}

.staff-panel__toolbar button:disabled {
    opacity: 0.5;
    cursor: not-allowed;
}

.staff-panel__list {
    display: grid;
    gap: 0.4rem;
    margin: 0;
    padding: 0;
    list-style: none;
}

.staff-panel__list li {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.55rem;
    padding: 0.5rem 0.6rem;
    border: 1px solid #edf0f3;
    border-radius: 0.5rem;
    background: #fbfcfe;
}

.staff-panel__item {
    display: grid;
    min-width: 0;
    gap: 0.1rem;
}

.staff-panel__item-title {
    overflow-wrap: anywhere;
    color: #344767;
    font-size: 0.8rem;
    font-weight: 700;
}

.staff-panel__item-meta {
    overflow-wrap: anywhere;
    color: #8492a2;
    font-size: 0.7rem;
}

.staff-panel__remove {
    display: inline-flex;
    flex: 0 0 1.75rem;
    width: 1.75rem;
    height: 1.75rem;
    align-items: center;
    justify-content: center;
    padding: 0;
    border: 1px solid #eed3d7;
    border-radius: 0.4rem;
    background: #fff;
    color: #b03a48;
    font-size: 0.68rem;
    cursor: pointer;
    transition: background-color 0.15s ease, border-color 0.15s ease;
}

.staff-panel__remove:hover,
.staff-panel__remove:focus {
    border-color: #d76a76;
    background: #fdf3f4;
    outline: none;
}

.staff-panel__empty {
    margin: 0;
    padding: 0.6rem;
    border: 1px dashed #e2e8f0;
    border-radius: 0.5rem;
    color: #9aa6b3;
    font-size: 0.74rem;
    text-align: center;
}

@media (max-width: 575.98px) {
    .staff-card__head {
        flex-wrap: wrap;
    }

    .staff-card__actions {
        width: 100%;
    }

    .staff-card__actions > button {
        flex: 1 1 auto;
        justify-content: center;
    }

    .staff-card__panels {
        padding: 0.8rem;
    }
}
</style>
